<template>
  <div class="effect-chain-editor">
    <div class="case-logic-builder-container">
      <case-logic
        :editor-id="editorId"
        :case-conf="effectChainCaseConf"
        :advanced-controls="false"
        :user="user"
        :readonly="readonly"
        :is-global="!caseConf"
        :available-nodes="['Timer', 'Check', 'Effect', 'Frame']"
        @update="({caseLogicDescription}) => updateEffectChain(caseLogicDescription)"
      />
      <slot />
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import random from 'lodash/random';
import get from 'lodash/get';
import diff from 'deep-diff';

export default {
  name: 'effect-chain-editor',
  components: {
    // we have to do this because of a circular dependency between
    // the effect chain editor and the case logic
    CaseLogic: () => import('../CaseLogic.vue'),
  },
  props: {
    editorId: {
      type: String,
      default: null,
    },
    effectChain: {
      type: Object,
      default: undefined,
    },
    baseEffectChain: {
      type: Object,
      default: undefined,
    },
    caseConf: {
      type: Object,
      required: false,
      default: undefined,
    },
    user: {
      type: Object,
      required: false,
      default: undefined,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    effectChainNodes() {
      if (this.effectChain && this.effectChain.type === 'CUSTOM_EFFECT_CHAIN') {
        return this.effectChain.nodes;
      } else if (this.baseEffectChain) {
        return this.baseEffectChain.nodes;
      }
      return {};
    },
    effectChainCaseConf() {
      return {
        ...(this.caseConf || {}),
        id:
          (this.baseEffectChain && this.baseEffectChain.id) ||
          (this.effectChain && this.effectChain.id) ||
          String(random(100, 1000)),
        caseLogicDescription: {
          nodes: this.effectChainNodes,
          customStates: get(this.caseConf, 'caseLogicDescription.customStates'),
        },
      };
    },
  },
  watch: {
    'baseEffectChain'(baseEffectChain) {
      this.$emit('baseEffectChainChange', baseEffectChain);
    },
  },
  created() {
    if (
      !this.effectChain ||
      this.effectChain.type === 'EXISTING_EFFECT_CHAIN'
    ) {
      this.currentEffectChain = cloneDeep(this.baseEffectChain) || { nodes: {} };
    } else {
      this.currentEffectChain = cloneDeep(this.effectChain) || { nodes: {} };
    }
    this.$emit('load', this.currentEffectChain);
    this.$emit('baseEffectChainChange', this.baseEffectChain);
  },
  methods: {
    updateEffectChain(caseLogicDescription) {
      // avoid calling update when nothing has changed, to avoid
      // an existing effect chain from becoming a custom effect chain
      // when nothing has changed
      const diffs = diff(caseLogicDescription.nodes, this.currentEffectChain.nodes) || [];
      const filteredDiffs = diffs.filter(diff => {
        // Sometimes diffs in case logic will be due
        // to undefined values becoming null or vice
        // versa, we want to ignore those
        if (diff.kind === 'D' && diff.lhs === null) {
          return false;
        }
        return true;
      })
      if (
        this.currentEffectChain &&
        filteredDiffs.length === 0
      ) {
        return;
      }
      this.$emit('update', {
        id:
          (this.effectChain && this.effectChain.id) ||
          (this.baseEffectChain && this.baseEffectChain.id) ||
          String(random(100, 1000)),
        type: 'CUSTOM_EFFECT_CHAIN',
        nodes: caseLogicDescription.nodes,
        updatedAt: new Date(),
      });
      this.currentEffectChain = cloneDeep(caseLogicDescription);
    },
  },
};
</script>

<style scoped>
.effect-chain-editor {
  height: 100%;
}

.case-logic-builder-container {
  height: 100%;
}
</style>
